import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { backpackAccountCategory } from '../constants'
import { createClient } from '../createClient'
import {
  type BorrowLendMovement,
  borrowLendMovementsResponseSchema,
} from './schema'

/** 1 ページあたり API が返す最大件数 (Backpack 仕様の上限) */
const PAGE_LIMIT = 1000

/**
 * 安全装置として 1 種別あたりに取得する最大ページ数。
 * 1000 件 × 1000 ページ = 100 万件まで耐える設計とする。
 */
const MAX_PAGES = 1000

/** 取得対象のイベント種別 (Lend / LendRedeem は取引所内の残高移動のため対象外) */
const TARGET_EVENT_TYPES = ['Borrow', 'BorrowRepay'] as const

/**
 * naive ISO date-time を UTC として解釈し UNIX milliseconds に変換する。
 * @param value - Backpack の timestamp 文字列
 * @returns UNIX milliseconds
 */
const parseTimestamp = (value: string): number => {
  const hasTimezone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(value)
  const isoString = hasTimezone ? value : `${value}Z`
  const ms = Date.parse(isoString)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid Backpack borrow lend timestamp: ${value}`)
  }
  return ms
}

/**
 * borrow lend position ごとの loanId を生成する。
 * @param params.positionId - Backpack の borrow lend position ID
 * @returns loanId
 */
const createLoanId = ({ positionId }: { positionId: string }): string =>
  `${backpackAccountCategory}_borrowLend_${positionId}`

/**
 * Backpack の borrow lend 履歴から借入 (`borrow`) / 返済 (`repayment`) Action を生成する。
 *
 * - `Borrow`: 借入 (`borrow`, in)。出金時の自動借入 (source: Withdrawal) や spot margin 等を含む。
 * - `Borrow` かつ source が `Interest`: 借入利息の元本組入れのため Action を生成しない。
 * - `BorrowRepay`: 返済 (`repayment`, out)。元本に組み入れられた利息を含む返済額全額。
 *
 * Backpack では借入利息が借入元本に加算され、返済額に利息が含まれる。
 * 会計処理では同じ loanId の借入額を超えた返済額が自動で `interest-fee` になるため、
 * 借入は元本のみ計上し、利息は返済時に経費として計上される。
 * (`interest-history` 側でも借入利息 (`Borrow` / `EntryFee`) は計上しない)
 *
 * 当 endpoint は `from`/`to` フィルタを持たず offset ベースの pagination のみ。
 * イベント種別ごとに sortDirection=Desc (新しい順) で取得し、 framework から渡された `from` より古い
 * record に達したら early-stop することで効率化する。
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  createHeaders,
  from,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const collected: (BorrowLendMovement & { timestampMs: number })[] = []

  for (const eventType of TARGET_EVENT_TYPES) {
    for (let page = 0; page < MAX_PAGES; page++) {
      const response = await client.run({
        method: 'GET',
        path: '/wapi/v1/history/borrowLend',
        query: {
          limit: PAGE_LIMIT.toString(),
          offset: (page * PAGE_LIMIT).toString(),
          sortDirection: 'Desc',
          type: eventType,
        },
        responseSchema: borrowLendMovementsResponseSchema,
      })

      let earlyStop = false
      for (const item of response) {
        const timestampMs = parseTimestamp(item.timestamp)
        // sortDirection=Desc のため timestamp は逐次古くなる。
        // pending 開始時刻 `from` より古くなった時点で以降は対象外なので終了。
        if (from !== undefined && timestampMs < from) {
          earlyStop = true
          break
        }
        // 未来側のフィルタ (to 指定がある場合) は client-side で除外
        if (to !== undefined && timestampMs > to) {
          continue
        }
        collected.push({ ...item, timestampMs })
      }

      if (earlyStop || response.length < PAGE_LIMIT) {
        break
      }
    }
  }

  collected.sort((a, b) => a.timestampMs - b.timestampMs)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of collected) {
    fetchedFrom =
      fetchedFrom === undefined
        ? item.timestampMs
        : Math.min(fetchedFrom, item.timestampMs)
    fetchedTo =
      fetchedTo === undefined
        ? item.timestampMs
        : Math.max(fetchedTo, item.timestampMs)

    if (
      (item.eventType !== 'Borrow' && item.eventType !== 'BorrowRepay') ||
      item.quantity.value <= 0n
    ) {
      continue
    }
    // 借入利息の元本組入れは返済時に interest-fee として計上されるため除外する
    if (item.eventType === 'Borrow' && item.source === 'Interest') {
      continue
    }

    const asset = getAsset(item.symbol)
    if (!asset) {
      throw new AssetNotFoundException(item.symbol)
    }

    const isBorrow = item.eventType === 'Borrow'

    actions.push({
      comment: `${item.eventType} (${item.source}) / position: ${item.positionId} / position quantity: ${item.positionQuantity}`,
      evidence: 'system-rule',
      loanId: createLoanId({ positionId: item.positionId }),
      order: 0,
      source: `${backpackAccountCategory}_${config.type}_${item.positionId}_${item.eventType}_${item.source}_${item.timestampMs}_${item.positionQuantity}`,
      type: isBorrow ? 'borrow' : 'repayment',
      timestamp: item.timestampMs,
      transfers: [
        {
          asset,
          amount: item.quantity,
          direction: isBorrow ? 'in' : 'out',
        },
      ],
    })
  }

  return {
    actions,
    count: collected.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
